import {
  Box,
  Pagination,
  Icon,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
  Timestamp,
  Timezone,
  Batch,
  TableProps,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { Title } from 'UI';
import { parseISO, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchUploadedFiles from '@/hooks/useApi/useFetchUploadedFiles';
import BulkUploadForm from './BulkUploadForm';
import styles from './FileUploadHistory.module.scss';
import { Button, TableFiltersContainer, TransactionStatus, Table } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';
import NoRecordSvg from './NoRecord.svg';
import { format } from 'date-fns';

type QueryParams = {
  transactionType: string;
  paymentType: string;
  timeZone: string;
  startDate: string;
  endDate: string;
};

type BulkUploadSearchParams = {
  transactionType?: string;
  paymentType?: string;
  timeZone?: string;
  timeRange?: [Timestamp, Timestamp];
};

type MyBatch = Batch & { index: number };

type FC = FunctionComponent;

const BulkUploadTable: FC = () => {
  const { response } = useClientData();
  const location = useLocation();
  const { userSettings } = useUserSettingsProvider();
  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      paymentType: params.get('paymentType') || '',
      transactionType: params.get('transactionType') || '',
      timeZone: params.get('timeZone') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
    };
  }, [location.search]);

  const paymentType = useMemo(() => queryParams.paymentType || '', [queryParams]);
  const transactionType = useMemo(() => queryParams.transactionType || '', [queryParams]);
  const timeZone = useMemo(() => (queryParams.timeZone as Timezone) || userSettings.timezone || 'UTC', [queryParams]);

  const timeDifference = useMemo(() => formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const startDate = useMemo(() => queryParams.startDate || timeDifference, [queryParams]);
  const endDate = useMemo(() => queryParams.endDate || currentDateTime, [queryParams]);

  const filtersApplied = useMemo(() => {
    return (
      !!paymentType ||
      !!transactionType ||
      !!(queryParams.timeZone && queryParams.timeZone !== userSettings.timezone) ||
      startDate !== timeDifference ||
      endDate !== currentDateTime
    );
  }, [
    paymentType,
    transactionType,
    queryParams.timeZone,
    userSettings.timezone,
    startDate,
    endDate,
    timeDifference,
    currentDateTime,
  ]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const [initialLoad, setInitialLoad] = useState(false);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<Batch[]>([]);
  const [uploadedFiles, , , fetchUploadedFiles] = useFetchUploadedFiles({});
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const alertDialog = useAlertDialog();

  // pagination
  const totalDataLength = useMemo(() => {
    return uploadedFiles?.PaginationMetaData?.TotalRecords || 0;
  }, [uploadedFiles?.Batches]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: BulkUploadSearchParams = {}) => {
    const defaultValues = {
      PortalUser: response.LoginUserName,
      FromDate: searchParams.timeRange?.[0]
        ? format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd')
        : startDate || timeDifference,
      ToDate: searchParams.timeRange?.[1]
        ? format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd')
        : endDate || currentDateTime,
      ...(searchParams.paymentType === 'all' ? {} : { PaymentType: searchParams.paymentType }),
      ...(searchParams.transactionType === 'all' ? {} : { TransactionType: searchParams.transactionType }),
      Timezone: searchParams.timeZone ?? timeZone,
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchUploadedFiles(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const scheduledTransactionRequest = Object.values(res.Batches || {});
      setFilteredList(scheduledTransactionRequest);
      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('FetchUploadedFilesError.defaultMessage');
      alertDialog.error(t('FetchUploadedFilesError.title'), msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams = {
      paymentType,
      transactionType,
      timeZone,
      startDate: startDate || timeDifference,
      endDate: endDate || currentDateTime,
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  const tableProps: TableProps<MyBatch> = {
    id: 'fileUpload-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'submitDate',
        header: t('Table.submitDate'),
        align: 'left',
        field: 'DateAdded',
      },
      {
        id: 'fileName',
        header: t('Table.fileName'),
        align: 'left',

        template: ({ data }) => (
          <Table.Link
            to={{
              pathname: `/file-upload-history/details/${data.BatchTransactionRequestID}`,
            }}
          >
            {data.BatchName}
          </Table.Link>
        ),
      },
      {
        id: 'transactionType',
        header: t('Table.transactionType'),
        align: 'left',
        template: ({ data }) => (data.TransactionType === 'RTR' ? 'VoPayInstant' : data.TransactionType),
      },
      {
        id: 'paymentType',
        header: t('Table.paymentType'),
        align: 'left',
        field: 'PaymentType',
      },
      {
        id: 'numberOfRecords',
        header: t('Table.numberOfRecords'),
        align: 'left',
        template: ({ data }) => data.NumRecords.toString(),
      },
      {
        id: 'totalAmount',
        header: t('Table.totalAmount'),
        align: 'left',
        template: ({ data }) => `$${formattedCurrency(data.TotalAmount.toString())}`,
      },

      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status="successful"
            label={Number(data.TotalSubmitted) === 0 ? 'Processed' : 'Submitted'}
          />
        ),
      },
    ],
    rows: filteredList.map((file, index) => ({
      id: String(file.BatchTransactionRequestID),
      data: { ...file, index },
    })),
  };

  const generateCSV = (filteredLists: Batch[]): void => {
    // Create the header for the CSV file
    const csvHeader = [
      'File Name',
      'Transaction Type',
      'Payment Type',
      'Number of Records',
      'Total Amount',
      'Submit Date',
      'Status',
    ];

    // Prepare the rows
    const rows = filteredLists.map((file) => [
      file.BatchName,
      file.TransactionType === 'RTR' ? 'VoPayInstant' : file.TransactionType,
      file.PaymentType,
      file.NumRecords.toString(),
      `$${formattedCurrency(file.TotalAmount.toString())}`,
      file.DateAdded,
      Number(file.TotalSubmitted) === 0 ? 'Processed' : 'Submitted',
    ]);

    // Create worksheet from rows
    const worksheet = XLSX.utils.aoa_to_sheet([csvHeader, ...rows]);

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Write the workbook to a CSV file
    XLSX.writeFile(workbook, 'file-upload-history.csv', { bookType: 'csv' });
  };

  return (
    <Box flx="col" gap="sm">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="fe" ai="center" base="8" gap="md">
          <TableFiltersContainer.Trigger />
          <Button
            id="export-group"
            colorScheme="secondary"
            size="smallest"
            onClick={() => {
              generateCSV(filteredList);
            }}
          >
            {tGlobal('Buttons.export')}
          </Button>
        </Box>
        <TableFiltersContainer.Content>
          <BulkUploadForm
            paymentType={paymentType}
            transactionType={transactionType}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            timeZone={timeZone}
            initialTimeRange={[
              parseISO(startDate || timeDifference).getTime() as Timestamp,
              parseISO(endDate || currentDateTime).getTime() as Timestamp,
            ]}
            setShouldShowEndDateError={setShouldShowEndDateError}
            search={(searchParams) => {
              const offset = setPageIndex(0);
              updateQueryParams({
                paymentType: searchParams.paymentType || '',
                transactionType: searchParams.transactionType || '',
                timeZone: searchParams.timeZone || '',
                startDate: format(new Date(searchParams.timeRange?.[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange?.[1]), 'yyyy-MM-dd'),
              });
              search(offset, searchParams);
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>
      <Box>
        <div className={styles.tableContainer}>
          <Table {...tableProps} />
        </div>
      </Box>

      {!initialLoad && (
        <Box flx="col" mt="lg" jc="center">
          <Box ai="center" jc="center">
            <Icon icon="search2" size="lg" />
          </Box>
          <Box ai="center" jc="center" pt="md">
            {t('noSearchMessage')}
          </Box>
        </Box>
      )}
      {initialLoad && filteredList && Object.values(filteredList).length === 0 && (
        <Box flx="col" gap="ml" mt="xl" mb="xl" pt="lg" pb="lg" base="8" jc="center">
          <Box ai="center" jc="center">
            <img src={NoRecordSvg} alt="No records" width="73" height="90" />
          </Box>
          <Box flx="col" gap="sm" base="8" jc="center">
            <Title level="3" emphasized style={{ textAlign: 'center' }}>
              {t('noRecordMessage')}
            </Title>
            <div className={styles.noRecordMessageDescription}>{t('noRecordMessageDescription')}</div>
          </Box>
        </Box>
      )}
      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              const searchParams = {
                paymentType: paymentType,
                transactionType: transactionType,
                timeZone: timeZone,
                startDate: startDate || timeDifference,
                endDate: endDate || currentDateTime,
              };
              search(offset, searchParams);
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default BulkUploadTable;
